// MACHINERY V2 (Stroje) — "Pridať dokumenty" (M2.5).
//
// Spec: data package §6 `[form] file upload (create only)`. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx `DocsUploadDialog`;
// artboard 1004. The pixels live in docsUploadParts.tsx (300-line cap); this
// file owns reading, validating and submitting the files.
//
// THREE THINGS THIS SURFACE MUST KEEP (all from §6):
//   1. EACH FILE BECOMES ITS OWN DOCUMENT — one POST per queued file, exactly
//      as v1's CreateDocumentDialog.tsx:50-55 loops the array. Not a batch.
//   2. THE CAP IS 11 MB PER FILE, and it is this surface's own number. SR-create
//      uses 10 (components/CreateServiceReportPage.tsx:154). Ticket §5 #6 says
//      keep them per-surface; do NOT unify them.
//   3. ANY FILE TYPE — v1 sets no `accept` attribute, and the artboard says
//      "ľubovoľný typ súboru" in as many words.
//
// DELIBERATE BEHAVIOUR CHANGE, FROM THE ARTBOARD: an oversize file is skipped,
// not fatal. v1's UploadDocument.tsx:79-83 calls `setValue("documents", [])` at :80 —
// one 46 MB video wipes the four files already queued. Artboard 1004 draws the
// other behaviour explicitly: the oversize file is named in an amber strip and
// "nebol pridaný do radu" (was not added to the queue) while the good files stay
// listed. The designer files define behaviour (ticket golden rule) and this one
// loses no work, so v2 follows the artboard.
//
// THE SUBMIT PATH NOW KEEPS THAT SAME PROMISE (M2.5 review finding R2). It did
// not: `submitUpload` caught every per-file rejection internally and always
// resolved, so `close()` ran unconditionally — a backend 500 on five queued
// files wiped the queue AND the hand-edited per-file names, leaving only a
// toast, in the one dialog whose rationale above is "loses no work". The action
// now returns the failed INDICES (useDocsTabActions.ts), and this dialog CLOSES
// ONLY WHEN EVERY QUEUED FILE SUCCEEDED. On a partial failure the successful
// rows are dropped — they exist on the server, and re-sending them would create
// duplicates — while the failed rows stay listed with their names, each row is
// marked, a RetryNotice names how many failed, and the primary button re-submits
// exactly those. Cancel is the only other way out and it is deliberate.
//
// DELIBERATE ADDITION, FROM THE DATA PACKAGE: the per-file NAME input. Artboard
// 1004 omits it, but §6 lists `[form] document name` as Editable + Required with
// a maxLength(50) rule, and the golden rule is that where the artboard omits a
// field the data package lists, the data package wins. It is pre-filled from the
// file name, so the common case is still one click.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ChangeDocument } from "../../../models/machineDocumentsModel";
import RetryNotice from "../../feedback/RetryNotice";
import TabDialog from "./TabDialog";
import { docNameError, DOC_NAME_MAX } from "./docsNameField";
import { QueuedFile, QueuedFileRow, UploadDropzone, UploadRejections } from "./docsUploadParts";
import { formatDocSize } from "./tabFormat";

/**
 * 11 MB. Restated rather than imported: `MAX_FILE_SIZE_MB` lives in
 * shared/models/models.ts, whose FIRST LINE is `import { AlertColor,
 * AlertPropsColorOverrides } from "@mui/material"` — importing a value from it
 * would put MUI in the v2 import graph, which §3 forbids. Same call, and the
 * same reasoning, as the Wave-1 `V2_DOC_TYPE_KEYS` duplicate.
 */
export const DOC_MAX_FILE_SIZE_MB = 11;

const baseName = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(0, dot) : fileName;
};

const readAsDataUrl = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

export interface DocsUploadDialogV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves with the INDICES (into `documents`) whose create failed. An empty
   *  array is the ONLY outcome that closes this dialog (R2). */
  onSubmit: (documents: ChangeDocument[]) => Promise<number[]>;
}

const DocsUploadDialogV2: React.FC<DocsUploadDialogV2Props> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const { translate } = useTranslation("myMachinery");
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [rejections, setRejections] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Keys of the rows whose POST failed on the last attempt (R2). */
  const [failedKeys, setFailedKeys] = useState<string[]>([]);

  const accept = async (files: File[]) => {
    if (!files.length) return;
    const tooBig: string[] = [];
    const unreadable: string[] = [];
    const added: QueuedFile[] = [];

    for (const f of files) {
      if (f.size > DOC_MAX_FILE_SIZE_MB * 1024 * 1024) {
        tooBig.push(f.name + " (" + formatDocSize(f.size) + ")");
        continue;
      }
      // Sequential on purpose: a bulk drop of large files read in parallel
      // holds every data-URI in memory at once.
      // eslint-disable-next-line no-await-in-loop
      const content = await readAsDataUrl(f);
      if (!content) {
        unreadable.push(f.name);
        continue;
      }
      added.push({
        // File objects have no identity of their own and two files can share a
        // name; the counter keeps rows distinct without leaning on the index.
        key: `${f.name}:${f.size}:${f.lastModified}:${added.length}:${Date.now()}`,
        name: baseName(f.name).slice(0, DOC_NAME_MAX),
        file: { name: f.name, content, size: f.size, createDate: new Date() },
      });
    }

    const lines: string[] = [];
    if (tooBig.length) {
      lines.push(
        translate("myMachinery:v2.tabs.docs.tooBig", {
          names: tooBig.join(", "),
          mb: DOC_MAX_FILE_SIZE_MB,
        })
      );
    }
    if (unreadable.length) {
      lines.push(
        translate("myMachinery:v2.tabs.docs.unreadable", { names: unreadable.join(", ") })
      );
    }

    if (added.length) setQueue((prev) => [...prev, ...added]);
    setRejections(lines);
  };

  const close = () => {
    setQueue([]);
    setRejections([]);
    setFailedKeys([]);
    setSubmitted(false);
    onOpenChange(false);
  };

  const invalid = queue.some((q) => docNameError(q.name) !== null);

  const submit = async () => {
    setSubmitted(true);
    if (!queue.length || invalid || busy) return;
    setBusy(true);
    setFailedKeys([]);
    // The rows as they were SENT: the failed indices index into this array, not
    // into whatever the queue becomes afterwards.
    const sent = queue;
    try {
      const failed = await onSubmit(sent.map((q) => ({ name: q.name.trim(), file: q.file })));
      if (failed.length === 0) {
        close();
        return;
      }
      // Only the failures stay queued. The rest were created server-side and
      // re-sending them would duplicate the documents.
      const kept = failed.map((i) => sent[i]).filter((q) => !!q);
      setQueue(kept);
      setFailedKeys(kept.map((q) => q.key));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TabDialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={translate("myMachinery:v2.tabs.docs.uploadTitle")}
      glyph={<span style={{ fontSize: 13 }}>&#9633;</span>}
      width={600}
      footerNote={translate("myMachinery:v2.tabs.docs.uploadNote")}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>
            {translate("common:buttons.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={submit}
            disabled={busy || queue.length === 0}
          >
            {translate("myMachinery:v2.tabs.docs.uploadCta", { count: queue.length })}
          </button>
        </>
      }
    >
      <UploadDropzone maxMb={DOC_MAX_FILE_SIZE_MB} onFiles={accept} />

      {failedKeys.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <RetryNotice
            title={translate("myMachinery:v2.tabs.docs.uploadPartialTitle", {
              count: failedKeys.length,
            })}
            hint={translate("myMachinery:v2.tabs.docs.uploadPartialHint")}
            onRetry={submit}
            busy={busy}
          />
        </div>
      )}

      {queue.map((item) => (
        <QueuedFileRow
          key={item.key}
          item={item}
          showErrors={submitted}
          failed={failedKeys.indexOf(item.key) >= 0}
          onRename={(key, name) =>
            setQueue((prev) => prev.map((q) => (q.key === key ? { ...q, name } : q)))
          }
          onRemove={(key) => setQueue((prev) => prev.filter((q) => q.key !== key))}
        />
      ))}

      <UploadRejections lines={rejections} />
    </TabDialog>
  );
};

export default DocsUploadDialogV2;
